import React, { useEffect, useMemo } from "react"

import { useJsApiLoader } from "@react-google-maps/api"
import type {
    DeepMap,
    FieldError,
    FieldValues,
    UseFormRegister,
    UseFormSetValue,
} from "react-hook-form"
import usePlacesAutocomplete, { getDetails } from "use-places-autocomplete"

import type { IIndexable } from "@common-shared/types/general.types"
import type { IAddress } from "@common-shared/types/responseData/commonResponseData.types"

import type {
    IGoogleAddressLookupSuggestion,
    IGoogleAddressPlaceResult,
} from "@front-shared/types/elements.types"

import { AutoCompleteElement } from "./autoComplete.element"

const GOOGLE_MAPS_NEEDED_LIBRARIES = ["places"] as (
    | "core"
    | "maps"
    | "places"
    | "geocoding"
    | "routes"
    | "marker"
    | "geometry"
    | "elevation"
    | "streetView"
    | "journeySharing"
    | "drawing"
    | "visualization"
)[]

// Any here in props so Google maps wrapper works fine
export const GoogleAddressLookupElement: React.FunctionComponent<
    IGoogleAddressLookupProps
> = props => {
    const { isLoaded: isJsGoogleMapsLoaded } = useJsApiLoader({
        id: "gmap-script-GoogleAddressLookupElement",
        googleMapsApiKey:
            props.googleMapsApiKey ||
            import.meta.env.VITE_GOOGLE_MAPS_KEY ||
            "",
        libraries: GOOGLE_MAPS_NEEDED_LIBRARIES,
    })

    const {
        init: initPlacesAutoComplete,
        ready: isPlacesAutoCompleteReady,
        value,
        suggestions,
        setValue,
    } = usePlacesAutocomplete({
        // Disable initializing when the component mounts, default is true
        // Since we want to init it after our Google API loads
        initOnMount: false,
        requestOptions: {
            componentRestrictions: {
                country: ["us"],
            },
            types: ["address"],
        },
        debounce: 300,
    })

    // Once Google maps library loaded -> init auto complete
    // https://www.npmjs.com/package/use-places-autocomplete
    useEffect(() => {
        if (!isJsGoogleMapsLoaded) {
            return
        }

        initPlacesAutoComplete()
    }, [isJsGoogleMapsLoaded])

    // Make some actions when Google address becomes ready
    useEffect(() => {
        if (!isPlacesAutoCompleteReady) {
            return
        }

        props.onReady?.()
    }, [isPlacesAutoCompleteReady])

    const getRenderedSuggestion = (
        item: IGoogleAddressLookupSuggestion,
    ): {
        displayValue: string
        dropdownDisplayValue: string
        value: IGoogleAddressLookupSuggestion
    } => {
        return {
            displayValue: props.getGoogleAddressDisplayValue
                ? props.getGoogleAddressDisplayValue(item)
                : item.description,
            dropdownDisplayValue: item.description,
            value: item,
        }
    }

    const renderedSuggestions: {
        displayValue: string
        dropdownDisplayValue: string
        value: IGoogleAddressLookupSuggestion
    }[] = useMemo(() => {
        if (!isPlacesAutoCompleteReady || suggestions.status !== "OK") {
            return []
        }

        return suggestions.data.map(getRenderedSuggestion)
    }, [suggestions])

    useEffect(() => {
        if (!props.externalValue) {
            return
        }

        setValue(props.externalValue, false)
    }, [props.externalValue])

    const handleSearch = (search: string) => {
        // Update the keyword of the input element
        if (value === search) {
            return
        }

        setValue(search)

        props.onSearchChanged?.(search)
    }

    const onItemSelected = (item: IGoogleAddressLookupSuggestion) => {
        if (props.onSelected) {
            if (props.justSuggestionResult) {
                props.onSelected(item)
            } else {
                getDetails({ placeId: item.place_id })
                    .then(details =>
                        props.onSelected?.(
                            typeof details === "string" ? undefined : details,
                        ),
                    )
                    .catch(e => {
                        console.error(e)
                    })
            }
        }
    }

    return (
        <div className="relative w-full">
            <AutoCompleteElement
                name={props.name}
                disabled={!isPlacesAutoCompleteReady}
                className={props.className}
                label={props.label}
                placeholder={props.placeholder}
                onChange={handleSearch}
                itemRenderer={getRenderedSuggestion}
                onSelected={onItemSelected}
                defaultValue={props.predefined}
                reactHookFormRegister={props.reactHookFormRegister}
                reactHookFormErrors={props.reactHookFormErrors}
                reactHookFormSet={props.reactHookFormSet}
                validations={props.validations}
                renderedOptions={renderedSuggestions}
                externalValue={value}
            />
        </div>
    )
}

interface IGoogleAddressLookupProps {
    name: string
    googleMapsApiKey?: string
    className?: string
    label?: string
    placeholder?: string
    reactHookFormRegister?: UseFormRegister<FieldValues>
    reactHookFormSet?: UseFormSetValue<FieldValues>
    reactHookFormErrors?: DeepMap<FieldValues, FieldError>
    validations?: IIndexable // Validations object
    predefined?: IAddress
    onReady?: () => void
    onSelected?: (item?: IGoogleAddressPlaceResult) => void
    onSearchChanged?: (search: string) => void
    getGoogleAddressDisplayValue?: (
        item: IGoogleAddressLookupSuggestion,
    ) => string
    externalValue?: string
    justSuggestionResult?: boolean
}
